import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  makeStyles
} from '@material-ui/core';
import { ColorButton } from "./ColorButton";
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Axios from 'axios';
import Auth from './Auth';
import { grey } from '@material-ui/core/colors';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    minWidth: '400px',
    padding: theme.spacing(2)
  },
  uploadButton: {
    marginTop: theme.spacing(2)
  },
  fileInput: {
    display: 'none'
  },
  fileName: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  formControl: {
    minWidth: '100%'
  },
  newProjectOption: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontWeight: 500,
    backgroundColor: theme.palette.action.hover
  },
  actionButtons: {
    margin: theme.spacing(2, 2, 2)
  },
  submit: {
  },
  cancel: {
    color: 'white',
    backgroundColor: grey[400],
    '&:hover': {
      backgroundColor: grey[600],
    },
  },
  error: {
    color: 'red',
    marginTop: theme.spacing(1)
  },
  success: {
    color: 'green',
    marginTop: theme.spacing(1)
  }
}));

export default function UploadModal({ open, onClose }) {
  const classes = useStyles();
  const [file, setFile] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('new');
  const [newProject, setNewProject] = useState('');
  const [isNewProject, setIsNewProject] = useState(true);
  const [error, setError] = useState('');
  const [request, setRequest] = useState({ source: null, error: null });
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      getProjects(Auth.getUserID());

      // Check if the path contains project
      const projectIdMatch = window.location.pathname.match(/\/projects\/(\d+)(\/corrections\/)?/);

      if (projectIdMatch) {
        setSelectedProject(projectIdMatch[1]);
        setIsNewProject(false);
      }
    }
    return () => {
      if (request.source) {
        request.source.cancel();
      }
    };
  }, [open]);

  const getProjects = userId => {
    const url = `${process.env.REACT_APP_BASE_URL}/users/${userId}/projects`;
    const source = Axios.CancelToken.source();

    Axios.get(url, { cancelToken: source.token })
      .then(response => {
        setProjects(response.data);
        setRequest({ source: null, error: null });
      })
      .catch(error => {
        if (Axios.isCancel(error)) {
          setRequest({ source: null, error: null });
        } else {
          setRequest({ source: null, error: error });
          setError('Error al cargar los proyectos');
        }
      });

    setRequest({ source: source, error: null });
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    const validZipTypes = [
      'application/zip',
      'application/x-zip',
      'application/x-zip-compressed',
      'application/octet-stream'
    ];

    // Verificar la extensión del archivo
    const isZipExtension = selectedFile?.name.toLowerCase().endsWith('.zip');

    if (selectedFile && (validZipTypes.includes(selectedFile.type) || isZipExtension)) {
      setFile(selectedFile);
      setError('');
    } else {
      setFile(null);
      setError('Por favor, seleccione un archivo ZIP válido');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (!file) {
      setError('Por favor, seleccione un archivo');
      setLoading(false);
      return;
    }

    const userId = Auth.getUserID();
    let projectId;

    try {
      if (isNewProject) {
        if (!newProject.trim()) {
          setError('Por favor, ingrese un nombre para el proyecto');
          setLoading(false);
          return;
        }

        let projectCreateForm = new FormData();
        projectCreateForm.append('name', newProject);

        try {
          const projectResponse = await Axios.post(
            `${process.env.REACT_APP_BASE_URL}/users/${userId}/projects`,
            {
              name: newProject,
              userId: userId
            },
            { headers: { 'Content-Type': 'application/json' } }
          );

          projectId = projectResponse.data.id;
        } catch (error) {
          setError('Hubo un error al crear el nuevo proyecto, vuelve a intentarlo');
          setLoading(false);
          return;
        }
      } else {
        projectId = selectedProject;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', userId);
      formData.append('projectId', projectId);

      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/${userId}/projects/${projectId}/corrections/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.status === 201) {

        setSuccessMessage('Correción subida correctamente. Aguarde a ser redirigido...');

        let correctionDate = response.data.date;

        setTimeout(() => {
          window.location.href = `/projects/${projectId}/corrections/${correctionDate}`;
        }, 2000);
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response) {
        if (error.response.status === 400) {
          setError('Error en los datos enviados. Por favor, verifique la información.');
        } else if (error.response.status === 409) {
          setError('Ya existe un proyecto con ese nombre.');
        } else {
          setError('Error al procesar la solicitud. Por favor, intente nuevamente.');
        }
      } else if (error.request) {
        setError('Error de conexión. Por favor, verifique su conexión a internet.');
      } else {
        setError('Error al subir el archivo. Por favor, intente nuevamente.');
      }
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setSelectedProject('');
    setNewProject('');
    setIsNewProject(false);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Subir Corrección</DialogTitle>
      <DialogContent>
        <form className={classes.form} noValidate>
          <FormControl className={classes.formControl}>
            <InputLabel>Proyecto</InputLabel>
            <Select
              value={isNewProject ? 'new' : selectedProject}
              onChange={(e) => {
                if (e.target.value === 'new') {
                  setIsNewProject(true);
                  setSelectedProject('new');
                } else {
                  setIsNewProject(false);
                  setSelectedProject(e.target.value);
                }
              }}
            >
              <MenuItem
                value="new"
                className={classes.newProjectOption}
              >
                ✨ Crear nuevo Proyecto
              </MenuItem>
              <MenuItem value="" disabled style={{ fontSize: '0.8em', color: 'gray' }}>
                Proyectos existentes
              </MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isNewProject && (
            <TextField
              variant="outlined"
              required
              fullWidth
              label="Nombre del nuevo proyecto"
              value={newProject}
              onChange={(e) => setNewProject(e.target.value)}
              autoFocus
            />
          )}

          <input
            accept=".zip"
            className={classes.fileInput}
            id="contained-button-file"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="contained-button-file">
            <Button
              variant="contained"
              color="default"
              component="span"
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              Seleccionar Archivo ZIP
            </Button>
          </label>
          {file && (
            <Typography className={classes.fileName}>
              Archivo seleccionado: {file.name}
            </Typography>
          )}
          {error && (
            <Typography className={classes.error}>
              {error}
            </Typography>
          )}
          {successMessage && (
            <Typography className={classes.success} style={{ color: 'green' }}>
              {successMessage}
            </Typography>
          )}
        </form>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <ColorButton onClick={handleClose}
          color="secondary"
          variant="contained"
          disabled={loading}
          className={classes.cancel}
        >
          Cancelar
        </ColorButton>
        <ColorButton
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          className={classes.submit}
          disabled={loading}
        >
          {loading ? 'Cargando...' : 'Subir'}
        </ColorButton>
      </DialogActions>
    </Dialog>
  );
} 